import React, { useState, useRef, useEffect } from 'react';
import './InlineColorPicker.css';

const InlineColorPicker = ({ onColorChange, onClose }) => {
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);
  const [hexInputValue, setHexInputValue] = useState('');
  const pickerRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const lastTouchTime = useRef(0);

  const hslToRgb = (hVal, sVal, lVal) => {
    const h = hVal / 360;
    const s = sVal / 100;
    const l = lVal / 100;

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
      const m = l - c / 2;

      if (h < 1 / 6) {
        r = c; g = x; b = 0;
      } else if (h < 2 / 6) {
        r = x; g = c; b = 0;
      } else if (h < 3 / 6) {
        r = 0; g = c; b = x;
      } else if (h < 4 / 6) {
        r = 0; g = x; b = c;
      } else if (h < 5 / 6) {
        r = x; g = 0; b = c;
      } else {
        r = c; g = 0; b = x;
      }

      r += m;
      g += m;
      b += m;
    }

    r = Math.round(Math.max(0, Math.min(1, r)) * 255);
    g = Math.round(Math.max(0, Math.min(1, g)) * 255);
    b = Math.round(Math.max(0, Math.min(1, b)) * 255);

    return { r, g, b };
  };

  const [rgbValues, setRgbValues] = useState({ r: 255, g: 0, b: 0 });

  useEffect(() => {
    const { r, g, b } = hslToRgb(hue, saturation, lightness);
    setRgbValues({ r, g, b });
  }, [hue, saturation, lightness]);

  // Get coordinates from mouse or touch event
  const getEventCoordinates = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    }
    return { clientX: e.clientX, clientY: e.clientY };
  };

  const handleColorSquareClick = (e, returnValues = false) => {
    if (!pickerRef.current) return returnValues ? null : undefined;
    const rect = pickerRef.current.getBoundingClientRect();
    const { clientX, clientY } = getEventCoordinates(e);
    const x = Math.min(rect.width, Math.max(0, clientX - rect.left));
    const y = Math.min(rect.height, Math.max(0, clientY - rect.top));

    const s = Math.min(100, Math.max(0, (x / rect.width) * 100));

    const topLightness = 100 - (s / 100) * 50;
    const l = topLightness - (y / rect.height) * topLightness;

    const finalS = Math.min(100, Math.max(0, Math.round(s * 100) / 100));
    const finalL = Math.min(100, Math.max(0, Math.round(l * 100) / 100));

    setSaturation(finalS);
    setLightness(finalL);

    if (returnValues) {
      return { saturation: finalS, lightness: finalL };
    }
  };

  const handleHueSliderStart = (e) => {
    // For mouse events, skip if touch just happened
    if (e.type === 'mousedown' && Date.now() - lastTouchTime.current < 500) return;
    
    // Record touch time for touch events
    if (e.type === 'touchstart') {
      lastTouchTime.current = Date.now();
    }
    
    e.preventDefault();
    isDragging.current = true;
    const sliderElement = e.currentTarget;
    const rect = sliderElement.getBoundingClientRect();
    const { clientX } = getEventCoordinates(e);
    const x = Math.min(rect.width, Math.max(0, clientX - rect.left));
    const value = (x / rect.width) * 360;
    setHue(value);

    const handleMove = (moveEvent) => {
      if (!isDragging.current || !sliderElement) return;
      const moveRect = sliderElement.getBoundingClientRect();
      const { clientX: moveClientX } = getEventCoordinates(moveEvent);
      const moveX = Math.min(moveRect.width, Math.max(0, moveClientX - moveRect.left));
      const moveValue = (moveX / moveRect.width) * 360;
      setHue(moveValue);
    };

    const handleEnd = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  const handleHexColorInput = (e) => {
    e.stopPropagation();
    const input = e.target;
    const hexValue = input.value.trim();

    const hexPattern = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    if (hexValue && hexPattern.test(hexValue)) {
      const normalizedHex = hexValue.startsWith('#') ? hexValue : `#${hexValue}`;
      onColorChange(normalizedHex);
      input.value = '';
      setHexInputValue('');
    }
  };

  const getColorSquareBackground = () => {
    const rgb = (c) => `rgb(${c.r}, ${c.g}, ${c.b})`;

    const white = hslToRgb(hue, 0, 100);
    const baseColor = hslToRgb(hue, 100, 50);

    return {
      background: `
        linear-gradient(to bottom, transparent, black),
        linear-gradient(to right, ${rgb(white)}, ${rgb(baseColor)})
      `
    };
  };

  return (
    <div className="inline-color-picker-wrapper">
      <div className="inline-color-picker" onClick={(e) => e.stopPropagation()}>
        <div className="color-picker-main">
          <div
            ref={pickerRef}
            className="color-square"
            style={getColorSquareBackground()}
            onMouseDown={(e) => {
              // Skip if touch event just happened (prevents double trigger on touch devices)
              if (Date.now() - lastTouchTime.current < 500) return;
              
              isDragging.current = true;
              dragStartPos.current = { x: e.clientX, y: e.clientY };
              handleColorSquareClick(e);

              const handleMouseMove = (moveEvent) => {
                if (!isDragging.current) return;
                handleColorSquareClick(moveEvent);
              };

              const handleMouseUp = (upEvent) => {
                isDragging.current = false;
                // Check if it was a click (not a drag) - if mouse didn't move much, it's a click
                const moveDistance = Math.sqrt(
                  Math.pow(upEvent.clientX - dragStartPos.current.x, 2) +
                  Math.pow(upEvent.clientY - dragStartPos.current.y, 2)
                );
                if (moveDistance < 5) {
                  const colorValues = handleColorSquareClick(upEvent, true);
                  if (colorValues) {
                    const { r, g, b } = hslToRgb(hue, colorValues.saturation, colorValues.lightness);
                    const currentHex = `#${[r, g, b].map(x => {
                      const hexStr = Math.round(x).toString(16).toUpperCase();
                      return hexStr.length === 1 ? '0' + hexStr : hexStr;
                    }).join('')}`;
                    onColorChange(currentHex);
                  }
                }
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
              };

              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              lastTouchTime.current = Date.now();
              isDragging.current = true;
              const { clientX, clientY } = getEventCoordinates(e);
              dragStartPos.current = { x: clientX, y: clientY };
              handleColorSquareClick(e);

              const handleTouchMove = (moveEvent) => {
                moveEvent.preventDefault();
                if (!isDragging.current) return;
                handleColorSquareClick(moveEvent);
              };

              const handleTouchEnd = (upEvent) => {
                isDragging.current = false;
                const touch = upEvent.changedTouches?.[0];
                if (!touch) return;
                
                const endX = touch.clientX;
                const endY = touch.clientY;
                const moveDistance = Math.sqrt(
                  Math.pow(endX - dragStartPos.current.x, 2) +
                  Math.pow(endY - dragStartPos.current.y, 2)
                );
                if (moveDistance < 10) {
                  const colorValues = handleColorSquareClick({ touches: [{ clientX: endX, clientY: endY }] }, true);
                  if (colorValues) {
                    const { r, g, b } = hslToRgb(hue, colorValues.saturation, colorValues.lightness);
                    const currentHex = `#${[r, g, b].map(x => {
                      const hexStr = Math.round(x).toString(16).toUpperCase();
                      return hexStr.length === 1 ? '0' + hexStr : hexStr;
                    }).join('')}`;
                    onColorChange(currentHex);
                  }
                }
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
              };

              document.addEventListener('touchmove', handleTouchMove, { passive: false });
              document.addEventListener('touchend', handleTouchEnd);
            }}
          >
          </div>
          <div className="color-picker-controls">
            <div className="hue-slider-container">
              <div
                className="hue-slider"
                onMouseDown={handleHueSliderStart}
                onTouchStart={handleHueSliderStart}
              >
                <div
                  className="hue-selector"
                  style={{ left: `${(hue / 360) * 100}%` }}
                />
              </div>
            </div>
            <div className="rgb-inputs">
              <div className="rgb-input-group">
                <label>R</label>
                <input
                  type="number"
                  min="0"
                  max="255"
                  value={rgbValues.r}
                  readOnly
                />
              </div>
              <div className="rgb-input-group">
                <label>G</label>
                <input
                  type="number"
                  min="0"
                  max="255"
                  value={rgbValues.g}
                  readOnly
                />
              </div>
              <div className="rgb-input-group">
                <label>B</label>
                <input
                  type="number"
                  min="0"
                  max="255"
                  value={rgbValues.b}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <input
        type="text"
        className="hex-color-input"
        placeholder="[#bbadae]"
        value={hexInputValue}
        onChange={(e) => setHexInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault(); // Prevent form submission
            handleHexColorInput(e);
          }
        }}
        onBlur={handleHexColorInput}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

export default InlineColorPicker;

